import { useState, useCallback, useEffect } from 'react';
import api from '../services/api';

/**
 * Hook para gerenciar highlights (marca-texto) de um artigo
 */
const useHighlights = (articleId) => {
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Carregar highlights do artigo
  const fetchHighlights = useCallback(async () => {
    if (!articleId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/highlights/article/${articleId}`);
      setHighlights(response.data.data || []);
    } catch (err) {
      console.error('Erro ao carregar highlights:', err);
      setError(err.message);
      setHighlights([]);
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  // Adicionar highlight
  const addHighlight = useCallback(async (highlightData) => {
    try {
      const response = await api.post('/highlights', {
        articleId,
        ...highlightData
      });

      const newHighlight = response.data.data;

      // Atualizar estado local
      setHighlights(prev => {
        // Verificar se já existe
        const exists = prev.some(h => h._id === newHighlight._id);
        if (exists) {
          return prev.map(h => h._id === newHighlight._id ? newHighlight : h);
        }
        return [...prev, newHighlight];
      });

      return newHighlight;
    } catch (err) {
      console.error('Erro ao adicionar highlight:', err);
      throw err;
    }
  }, [articleId]);

  // Remover highlight
  const removeHighlight = useCallback(async (highlightId) => {
    try {
      await api.delete(`/highlights/${highlightId}`);

      // Atualizar estado local
      setHighlights(prev => prev.filter(h => h._id !== highlightId));
    } catch (err) {
      console.error('Erro ao remover highlight:', err);
      throw err;
    }
  }, []);

  // Atualizar highlight (cor ou nota)
  const updateHighlight = useCallback(async (highlightId, updates) => {
    try {
      const response = await api.put(`/highlights/${highlightId}`, updates);
      const updated = response.data.data;

      // Atualizar estado local
      setHighlights(prev =>
        prev.map(h => h._id === highlightId ? updated : h)
      );

      return updated;
    } catch (err) {
      console.error('Erro ao atualizar highlight:', err);
      throw err;
    }
  }, []);

  // Carregar highlights quando articleId muda
  useEffect(() => {
    if (articleId) {
      fetchHighlights();
    } else {
      setHighlights([]);
    }
  }, [articleId, fetchHighlights]);

  return {
    highlights,
    loading,
    error,
    addHighlight,
    removeHighlight,
    updateHighlight,
    refetch: fetchHighlights
  };
};

export default useHighlights;
